import styled, { css } from 'styled-components';
import { size } from '../../../../styled-components/layout/helpers';
import { noTransform } from '../../../../styled-components/layout/keyframes';
import { ButtonReset } from '../../../../styled-components/layout/reset';
import { NavIcon } from '../NavigationIcons';

export const Wrapper = styled.div`
  --_sidenav-p-block: ${size(3)};
  --_sidenav-p-inline: ${size(2)};

  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  align-items: center;
  gap: var(--_sidenav-gap, ${size(6)});
  width: var(--navigation-sidenav-width);
  height: 100%;
  padding: var(--_sidenav-p-block) var(--_sidenav-p-inline);
  background-color: var(--navigation-bg);
  overflow: auto;
  overscroll-behavior: contain;

  ${(props) => props.theme.breakpoint.LtMd} {
    --_sidenav-gap: ${size(1.5)};
  }

  ${(props) => props.theme.breakpoint.Md} {
    @media (max-height: 900px) {
      --_sidenav-gap: ${size(1.5)};
    }
  }

  body:has(#hubspot-messages-iframe-container) & {
    --_hubspot-chat-height: 76px; // Height of the chat button + position offset (magic number)

    padding-block-end: calc(var(--_sidenav-p-block) + var(--_hubspot-chat-height));
  }
`;

export const ItemList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${size(1.5)};
  width: 100%;
`;

interface ItemProps {
  $isActive?: boolean;
}

export const Item = styled.div<ItemProps>`
  --_item-p-block: ${size(1)};
  --_item-p-inline: ${size(1.25)};
  --_item-gap: ${size(0.5)};
  --_item-notification-outline-color: var(--navigation-bg);

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--_item-gap);
  width: 100%;
  padding: var(--_item-p-block) var(--_item-p-inline);
  text-decoration: none;
  font-weight: var(--font-weight-regular);
  color: var(--navigation-fg); // Reset color if used as a link
  border-radius: ${size(1)}; // Add rounded borders to focus outline

  ${({ $isActive }) =>
    $isActive
      ? css`
          --_item-icon-bg: var(--navigation-active-bg);
        `
      : css`
          &:hover {
            --_item-icon-bg: var(--navigation-hover-bg);
          }
        `}
`;

export const ItemIcon = styled.div.attrs(() => ({
  'aria-hidden': true,
}))`
  --_icon-size: ${size(3)};

  position: relative;
  display: flex;
  justify-content: center;
  width: 100%;
  padding: ${size(0.5)};
  margin-top: calc(var(--_item-p-block) / -2); // Reduce Item padding top if present
  border-radius: 100vmax;
  background-color: var(--_item-icon-bg, transparent);
  font-size: var(--_icon-size);

  ${NavIcon} {
    transform-origin: 50%;
    transition: transform 200ms ease;
  }

  ${(props) => props.theme.breakpoint.Md} {
    ${Item}:hover & {
      ${NavIcon} {
        transform: scale(1.1);
      }
    }
  }
`;

// Action icon to used in Item
export const ItemAction = styled.div.attrs(() => ({
  'aria-hidden': true,
}))`
  --_itemadd-size: ${size(5)};

  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--_itemadd-size);
  height: var(--_itemadd-size);
  padding: ${size(1)};
  border-radius: calc(var(--_itemadd-size) * 0.4);
  background-color: var(--navigation-accent-bg);
  color: var(--navigation-accent-fg);

  ${NavIcon} {
    width: 100%;
    height: 100%;
  }

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    box-shadow:
      0 1px 2px rgba(0, 0, 0, 0.1),
      0 4px 4px rgba(0, 0, 0, 0.09),
      0 8px 5px rgba(0, 0, 0, 0.05),
      0 15px 6px rgba(0, 0, 0, 0.02);
    opacity: 0;
    transition: opacity 200ms ease;
  }

  &:hover {
    &::before {
      opacity: 1;
    }
  }
`;

export const ItemLabel = styled.span`
  display: block;
  text-align: center;
  font-size: ${(props) => props.theme.font.size.xsmall};
  line-height: ${(props) => props.theme.font.size.regular};
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: calc(
    100% + (var(--_item-p-inline) * 2) + (var(--_sidenav-p-inline))
  ); // Allow flow outside of Item but keep half sidenav padding on both sides
`;

export const Footer = styled.footer`
  margin-top: auto;
`;

export const Notification = styled.div`
  --_notification-p-block: ${size(0.25)};
  --_notification-p-inline: ${size(0.5)};

  position: absolute;
  top: ${size(-0.25)};
  right: 0;
  min-width: ${size(2)};
  padding: var(--_notification-p-block) var(--_notification-p-inline);
  border-radius: ${size(1)};
  background-color: var(--navigation-accent-bg);
  color: var(--navigation-accent-fg);
  font-size: ${(props) => props.theme.font.size.xxsmall};
  line-height: ${(props) => props.theme.font.size.xsmall};
  font-weight: var(--font-weight-bold);
  outline: ${size(0.25)} solid var(--_item-notification-outline-color);
  text-align: center;

  // Animation
  transform: scale(0);
  transform-origin: 50%;
  animation: ${noTransform} 200ms cubic-bezier(0.47, 1.64, 0.41, 0.8) forwards;
`;

export const CloseBtn = styled(ButtonReset)`
  flex-shrink: 0;
  position: sticky;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${size(4)};
  height: ${size(4)};
  background-color: var(--navigation-border-color);
  border-radius: 50%;
  font-size: ${size(3)};
  transform-origin: 50%;
  transition: transform 200ms ease;
  z-index: 10;

  &:active {
    transform: scale(0.85);
  }

  // Hitbox
  &::before {
    content: '';
    position: absolute;
    inset: ${size(-1)};
  }

  ${(props) => props.theme.breakpoint.Md} {
    display: none;
  }
`;
